import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { OperationalIcon } from "./operational-icon";

export interface InfoPopoverProps {
  readonly children: ReactNode;
  readonly label: string;
  readonly title?: string | undefined;
}

/** Compact, click-open help that keeps guidance out of the primary workflow. */
export function InfoPopover({ children, label, title }: InfoPopoverProps): ReactNode {
  const contentId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{
    readonly left: number;
    readonly maxHeight: number;
    readonly placement: "bottom" | "top";
    readonly top: number;
  } | null>(null);

  const updatePosition = useCallback((): void => {
    const trigger = triggerRef.current;
    const content = contentRef.current;
    if (trigger === null || content === null) return;

    const margin = 12;
    const gap = 6;
    const triggerRect = trigger.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const viewportWidth = globalThis.innerWidth;
    const viewportHeight = globalThis.innerHeight;
    const availableBelow = Math.max(0, viewportHeight - triggerRect.bottom - gap - margin);
    const availableAbove = Math.max(0, triggerRect.top - gap - margin);
    const desiredHeight = Math.max(content.scrollHeight, contentRect.height);
    const minimumUsefulHeight = Math.min(desiredHeight, 160);
    const placement = availableBelow >= minimumUsefulHeight || availableBelow >= availableAbove
      ? "bottom"
      : "top";
    const maxHeight = Math.max(1, placement === "bottom" ? availableBelow : availableAbove);
    const renderedHeight = Math.min(desiredHeight, maxHeight);
    const top = placement === "bottom"
      ? triggerRect.bottom + gap
      : Math.max(margin, triggerRect.top - gap - renderedHeight);
    const left = Math.min(
      Math.max(margin, triggerRect.right - contentRect.width),
      Math.max(margin, viewportWidth - contentRect.width - margin),
    );

    setPosition({ left, maxHeight, placement, top });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    updatePosition();
  }, [children, open, title, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const reposition = (): void => { updatePosition(); };
    const dismiss = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) || contentRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    globalThis.addEventListener("resize", reposition);
    document.addEventListener("scroll", reposition, true);
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", handleKey);
    return () => {
      globalThis.removeEventListener("resize", reposition);
      document.removeEventListener("scroll", reposition, true);
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open, updatePosition]);

  const popover = !open ? null : createPortal(
    <div
      aria-label={title ?? label}
      className="thw-info-popover__content"
      data-placement={position?.placement}
      id={contentId}
      ref={contentRef}
      role="note"
      style={position === null ? undefined : {
        left: position.left,
        maxHeight: position.maxHeight,
        top: position.top,
      }}
    >
      {title === undefined ? null : <strong>{title}</strong>}
      {children}
    </div>,
    document.body,
  );

  return <span className="thw-info-popover">
    <button
      aria-controls={contentId}
      aria-expanded={open}
      aria-label={label}
      className="thw-info-popover__trigger"
      onClick={() => { setOpen((current) => !current); }}
      ref={triggerRef}
      title={label}
      type="button"
    >
      <OperationalIcon name="info" />
    </button>
    {popover}
  </span>;
}
